// Core packages
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from "@angular/router";

// Third party packages
import { MatDialog, MatPaginator } from "@angular/material";
import { MatSort } from '@angular/material/sort';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { combineLatest, merge, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, finalize, startWith, take, tap } from "rxjs/operators";

// Core packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { TankService } from 'src/app/modules/tank/tank.service';
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ThemeService } from 'src/app/shared/services/theme.service';

import { MatOptionSelectionChange } from '@angular/material';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { ICompany } from 'src/app/shared/models/company/company.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { DateTimeFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import { ListsService } from 'src/app/shared/services/lists.service';
import { TankDataSource } from 'src/app/shared/utils/data-sources/tank-data-source';
import { AddDocumentToTankDialogComponent } from '../../components/add-document-to-tank-dialog/add-document-to-tank-dialog.component';
import { EditTankDocumentDialogComponent } from '../../components/edit-tank-document-dialog copy/edit-tank-document-dialog.component';

@Component({
  selector: 'app-tank-list',
  templateUrl: './tank-list.page.html',
  styleUrls: ['./tank-list.page.scss']
})
export class TankListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  // loggedUserMaster = false;
  dateTimeFormat: DateTimeFormatPipe;
  // activeSection: string; // Active document section
  // activeSectionData: any[] = [];
  // itemId: string; // Id of active mapping section item
  // condoStructure: any;
  // documentArchive: any;
  // documentArchiveId: string;
  // sortedDocuments: any[] = [];
  documentArchiveIsLoading = false;
  // public documentTypeStructure: DocumentTypeStructure = new DocumentTypeStructure();

  tank: any[] = [];
  sortedData = [];
  filterValue = "";
  displayedColumns: string[] = ['index', 'condo', 'name', 'createdAt', 'actions'];
  dataSource: TankDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  condoFilter: FormControl = new FormControl('');
  nameFilter: FormControl = new FormControl('');
  companyFilter: FormControl = new FormControl('');
  createdAtFilter: FormControl = new FormControl('');
  viewedFilter: FormControl = new FormControl('');
  documents: any[] = [];
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};

  availableStatus = ['yes', 'no'];
  companyIsLoading = false;
  availableCompanies: ICompany[] = [];

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private router: Router,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private tankService: TankService,
    private listsService: ListsService,
    private helperService: HelperService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
    private companiesService: CompaniesService,
  ) { }


  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.documentsPage.pageTitle")
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get logged user's role data
    this.currentRole = this.authService.loggedUserRequestedRoleType$.getValue();
    if (
      this.currentRole !== "admin" && this.currentRole !== "operator"
      && this.currentRole !== "master" && this.currentRole !== "collaborator"
    ) {
      this.toastrService.error(this.translateService.instant("common.errors.not_authorized"));
      this.router.navigate([this.lang, 'app']);
      return;
    }

    if (this.currentRole === 'admin' || this.currentRole === 'operator') {
      this.displayedColumns = ['condo', 'name', 'company', 'viewed', 'createdAt', 'actions'];
    }

    // Init data source
    this.dataSource = new TankDataSource(this.tankService, this.helperService, this.authService);

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings('suppliers');

    this.getAvailableCompanies();
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, 'keyup')
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.condoFilter.valueChanges.pipe(startWith(null as string)),
        this.nameFilter.valueChanges.pipe(startWith(null as string)),
        this.companyFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
        this.viewedFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(
          res => {
            this.companyIsLoading = true;
            this.getAvailableCompanies({
              general: this.companyFilter.value
            });
            // Load page only if at least one value is not null
            if (!res.every((value) => value === null)) {
              this.paginator.pageIndex = 0;
              this.loadPage();
            }
          }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'tank',
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        condo: this.condoFilter.value,
        name: this.nameFilter.value,
        company: this.companyFilter.value,
        createdAt: this.createdAtFilter.value,
        viewed: this.viewedFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      condo: this.condoFilter.value,
      name: this.nameFilter.value,
      company: this.companyFilter.value ? this.companyFilter.value._id : undefined,
      viewed: this.viewedFilter.value,
      createdAt: this.createdAtFilter.value ? new Date(this.createdAtFilter.value).toISOString() : undefined,
    };
    this.dataSource.loadItems(start, length, this.sort.active, this.sort.direction, search);
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.condoFilter.setValue(null);
    this.nameFilter.setValue(null);
    this.companyFilter.setValue(null);
    this.createdAtFilter.setValue(null);
    this.viewedFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = 'desc';
    this.sort.active = 'createdAt';
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.general) {
                  // this.input.nativeElement.value = setting.search.general;
                }
                if (setting.search && setting.search.condo) {
                  this.condoFilter.setValue(setting.search.condo);
                }
                if (setting.search && setting.search.name) {
                  this.nameFilter.setValue(setting.search.name);
                }
                if (setting.search && setting.search.company) {
                  this.companyFilter.setValue(setting.search.company);
                }
                if (setting.search && setting.search.createdAt) {
                  this.createdAtFilter.setValue(setting.search.createdAt);
                }
              }
            });
          }
        }
      )
    );
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayCompanyFn(arg?: ICompany): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Handle click on document and open dialog box to edit/show document data
   * and upload document file
   * modalTitle: title of dialogbox from document name
   * documentKey: doc type, es. AA1, AA2, etc.
   * documentId: the ID of document into array of documentArchive section (es. gestAmmTivoDipendenti)
   * subDocumentId: the ID of sub document into array of docs type (es. AA1)
   * itemData: data value of form items
   * action: edit or show doc data
   *
   * @since 1.0.0
   */
  onOpenEditDocDialog(itemData: any): void {
    const dialogRef = this.dialog.open(EditTankDocumentDialogComponent, {
      data: {
        document: itemData,
      },
      width: "600px"
    });

    dialogRef.beforeClosed()
      .pipe(
        take(1),
        finalize(() => this.loadPage())
      )
      .subscribe(
        res => {
          if (res && res.save) {
            this.toastrService.success(this.translateService.instant("companies.detail.documentsTab.updated_successfully"))
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      );
  }

  onOpenAddDocDialog() {
    // Add new document
    const dialogRef = this.dialog.open(AddDocumentToTankDialogComponent, {
      data: {
        model: this.model,
        title: this.translateService.instant(`suppliers.fields.documents.addNew`),
      },
      width: "600px",
    });

    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (res) {
            this.loadPage();
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableCompanies(search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getList(0, null, null, null, search)
        .pipe(
          finalize(() => {
            this.companyIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
            this.availableCompanies = res.data.items;
          }
        })
    );
  }
  
  /**
   * Handle click on a selected company in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onCompanyFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.companyFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }
}
